import type { MonopolyPayload } from '../report';
import { fmtDay, fmtDecimal, fmtInt, fmtPct, niceMax, share, type MonopolyTab } from '../utils';
import { AreaLine, Bar, Bars, KpiTile, Panel, Stat, Swatch } from './Primitives';

/*
 * Overview — the design's landing tab: the cards-used hero, four KPIs, and one
 * summary panel per deeper tab with a "View statistics →" link into it. Figures
 * that also live on a deeper tab are READ from that tab's section, so the
 * overview can never disagree with the detail.
 */
export function OverviewTab({ data, onTab }: { data: MonopolyPayload; onTab: (t: MonopolyTab) => void }) {
  const { overview: o, participants: p, visits: v, days, campaign } = data;
  const cu = o.cardsUsed;
  // Only physical cards are printed and reach our system; digital cards are
  // granted per e-commerce order. A digital share of the print run exists only
  // when the payload declares a digital print allotment (the design's sample).
  const digitalInRun = data.cards.printedDigital !== undefined;
  const physicalPct = share(cu.physical, cu.printed);
  const digitalPct = digitalInRun ? share(cu.digital, cu.printed) : 0;
  const redeemedPct = digitalInRun ? share(cu.total, cu.printed) : physicalPct;
  const physicalTotal = o.cardsIssued.physical.total;
  const issuedPhysPct = physicalTotal !== undefined ? share(o.cardsIssued.physical.used, physicalTotal) : 0;
  const issuedDigPct = share(o.cardsIssued.digital.opened, o.cardsIssued.digital.granted);
  const dice = o.dice;
  const throwsUsedPct = dice?.rolled !== undefined ? share(dice.rolled, dice.throwsEarned) : null;
  const rolledPct = dice ? share(dice.playersRolled, p.unique) : 0;
  const peak = p.newByDay.reduce((best, n, i) => (n > best.n ? { n, i } : best), { n: -1, i: 0 });
  const link = (t: MonopolyTab) => (
    <button type="button" className="mono-panel-link" onClick={() => onTab(t)}>
      Voir les statistiques →
    </button>
  );

  return (
    <>
      <div className="mono-grid-hero">
        <section className="mono-panel">
          <div className="mono-hero-head">
            <span className="mono-eyebrow">CARTES UTILISÉES — codes validés, tous canaux confondus</span>
            <span className="mono-day-badge">
              JOUR {campaign.currentDay} / {campaign.totalDays}
            </span>
          </div>
          <div className="mono-hero-row">
            <div className="mono-hero-value" data-testid="cards-used">
              {fmtInt(cu.total)}
            </div>
            <div className="mono-hero-side">
              <div className="mono-hero-rate">▲ {fmtInt(cu.perDay)} / jour</div>
              <div className="mono-note">
                sur {fmtInt(cu.printed)} cartes {digitalInRun ? '' : 'physiques '}imprimées
              </div>
            </div>
          </div>
          <div>
            <Bar
              segments={
                digitalInRun
                  ? [
                      { pct: physicalPct, fill: 'blue' },
                      { pct: digitalPct, fill: 'cyan' },
                    ]
                  : [{ pct: physicalPct, fill: 'blue' }]
              }
              label={digitalInRun ? 'Part du tirage utilisée, physique et digital' : 'Part du tirage physique utilisée'}
            />
            <div className="mono-legend" style={{ marginTop: 8 }}>
              <div className="items">
                <span>
                  <Swatch fill="blue" />
                  Codes physiques {fmtInt(cu.physical)}
                </span>
                <span>
                  <Swatch fill="cyan" />
                  Cartes digitales {fmtInt(cu.digital)}
                </span>
              </div>
              <span className="strong">
                {fmtPct(redeemedPct)} du tirage {digitalInRun ? '' : 'physique '}utilisé
              </span>
            </div>
          </div>
        </section>

        <section className="mono-panel">
          <div className="mono-eyebrow">CARTES UTILISÉES PAR JOUR</div>
          <AreaLine
            values={o.cardsUsedByDay}
            days={days}
            max={niceMax(o.cardsUsedByDay)}
            height={120}
            color="var(--mono-blue-700)"
            fill="var(--mono-blue-soft)"
            endDot
            labels="ends"
            ariaLabel="Cartes utilisées par jour"
          />
        </section>
      </div>

      <div className="mono-grid-4">
        <KpiTile label="Participants uniques" value={fmtInt(p.unique)} sub={`▲ ${fmtInt(o.participantsToday)} aujourd'hui`} tone="up" />
        <KpiTile label="Opt-ins (CGU acceptées)" value={fmtInt(p.optIns)} sub={`${fmtPct(share(p.optIns, p.unique))} des participants`} />
        <KpiTile label="Visites" value={fmtInt(v.total)} sub={`${fmtDecimal(v.sessionsPerVisitor)} sessions / visiteur`} />
        <KpiTile label="Cartes par participant" value={fmtDecimal(o.cardsPerParticipant, 1)} sub="codes utilisés en moyenne" />
      </div>

      <div className="mono-grid-2">
        <Panel title="Participants et opt-ins" action={link('participants')}>
          <Bars
            values={p.newByDay}
            days={days}
            max={niceMax(p.newByDay)}
            height={150}
            highlightLast
            seriesLabel="Nouveaux participants"
            note="Comptes créés ce jour-là (première authentification dans l'app)"
            ariaLabel="Nouveaux participants par jour"
          />
          <div className="mono-note">
            Nouveaux participants par jour · pic de lancement {fmtInt(peak.n)} le {days[peak.i] ? fmtDay(days[peak.i]) : '—'}
          </div>
        </Panel>

        <Panel title="Cartes distribuées et ouvertes" action={link('cards')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="mono-row-between">
                <span className="k">Cartes physiques — en magasin</span>
                <span className="v">
                  {fmtInt(o.cardsIssued.physical.used)}
                  {physicalTotal !== undefined && (
                    <>
                      {' '}
                      <span className="sep">/</span> {fmtInt(physicalTotal)}
                    </>
                  )}
                </span>
              </div>
              {/* The bar needs the physical print run; without it only the count is honest. */}
              {physicalTotal !== undefined ? (
                <Bar segments={[{ pct: issuedPhysPct, fill: 'blue' }]} thick label={fmtPct(issuedPhysPct)} />
              ) : (
                <div className="mono-note">codes saisis dans l&apos;app · tirage physique non publié</div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="mono-row-between">
                <span className="k">Packs digitaux — Drive / LAD / LEX</span>
                <span className="v">
                  {fmtInt(o.cardsIssued.digital.opened)} <span className="sep">/</span> {fmtInt(o.cardsIssued.digital.granted)} distribués
                </span>
              </div>
              <Bar segments={[{ pct: issuedDigPct, fill: 'cyan' }]} thick label={fmtPct(issuedDigPct)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, paddingTop: 4 }}>
            <div className="mono-mini">
              <div className="k">Taux d&apos;ouverture digital</div>
              <div className="v">{fmtPct(issuedDigPct)}</div>
            </div>
            <div className="mono-mini">
              <div className="k">Codes refusés</div>
              <div className="v">{fmtPct(o.cardsIssued.codesRejectedPct)}</div>
            </div>
          </div>
        </Panel>
      </div>

      {/* Only once the dice ledger has real throws — a missing panel is honest, a zero is not. */}
      {dice && (
        <Panel title="Lancer de dés et tours de plateau" action={link('cards')}>
          <div className="mono-grid-4">
            {/* A throw being used is not recorded anywhere, so the roll count
                appears only when the source has it; the board's step counter is
                always exact. */}
            {dice.rolled !== undefined ? (
              <Stat label="Dés lancés" value={fmtInt(dice.rolled)} sub={`sur ${fmtInt(dice.throwsEarned)} lancers gagnés`} tone="blue" />
            ) : (
              <Stat label="Cases parcourues" value={fmtInt(dice.steps ?? 0)} sub={`${fmtInt(dice.throwsEarned)} lancers gagnés`} tone="blue" />
            )}
            {dice.boardsCompleted !== undefined && (
              <Stat label="Tours de plateau terminés" value={fmtInt(dice.boardsCompleted)} sub="1 carte digitale offerte par tour" tone="red" />
            )}
            <Stat label="Joueurs ayant lancé" value={fmtInt(dice.playersRolled)} sub={`${fmtPct(rolledPct)} des participants`} />
            <Stat
              label={dice.avgRollsPerPlayer !== undefined ? 'Lancers moyens / joueur' : 'Cases moyennes / joueur'}
              value={fmtDecimal(dice.avgRollsPerPlayer ?? dice.avgStepsPerPlayer ?? 0, 1)}
              sub={dice.lapsPerPlayer !== undefined ? `${fmtDecimal(dice.lapsPerPlayer)} tours par joueur` : undefined}
            />
          </div>
          {throwsUsedPct !== null ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="mono-legend">
                <span>Lancers utilisés / gagnés (1 lancer pour 3 cartes utilisées)</span>
                <span className="strong">{fmtPct(throwsUsedPct)} utilisés</span>
              </div>
              <Bar segments={[{ pct: throwsUsedPct, fill: 'blue' }]} label="Lancers utilisés et gagnés" />
            </div>
          ) : (
            <div className="mono-note">
              1 lancer pour 3 cartes utilisées · les lancers consommés ne sont pas enregistrés, les cases et les tours du plateau sont affichés à la place
            </div>
          )}
        </Panel>
      )}

      <Panel title="Visites et sessions" action={link('visits')}>
        <div className="mono-grid-visits">
          <Bars
            values={v.byDay}
            days={days}
            max={niceMax(v.byDay)}
            height={180}
            tone="cyan"
            highlightLast
            seriesLabel="Visites"
            note="Une connexion compte comme visite s'il n'y a pas eu de connexion dans les 30 minutes précédentes"
            ariaLabel="Visites par jour"
          />
          <div className="mono-side-stats">
            <div>
              <span className="k">Visites totales</span>
              <span className="v">{fmtInt(v.total)}</span>
            </div>
            <div>
              <span className="k">Visiteurs uniques</span>
              <span className="v">{fmtInt(v.uniqueVisitors)}</span>
            </div>
            <div>
              <span className="k">Sessions / visiteur</span>
              <span className="v">{fmtDecimal(v.sessionsPerVisitor)}</span>
            </div>
            <div>
              <span className="k">Durée moyenne de session</span>
              <span className="v">{v.avgSession ?? '—'}</span>
            </div>
          </div>
        </div>
      </Panel>
    </>
  );
}
